import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import { InputText } from "primereact/inputtext";
import {
    findLatestGoodsPolicy,
    validateTicket,
    createGoods,
    getGoodsInformationByTicketNumber,
} from "../../../../services/counter/dashboard.service";
import { useToast } from "../../../../hooks/ToastContext";
import { getAPIErrorMessage } from "../../../../utility/utils";
import { twJoin } from "tailwind-merge";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

export default function GoodsManageModal({ visible, setVisible, companyId }) {
    const { toast } = useToast();
    const [ticketNo, setTicketNo] = useState("");
    const [units, setUnits] = useState("");
    const [grossPay, setGrossPay] = useState("");
    const [netPay, setNetPay] = useState("");
    const [finalPay, setFinalPay] = useState("");

    const [totalPay, setTotalPay] = useState("");
    const [refund, setRefund] = useState("");
    const [previouslyPaid, setPreviouslyPaid] = useState(0);

    const [perSeatFree, setPerSeatFree] = useState(2);
    const [rate, setRate] = useState(0);
    const [isManualFareChange, setIsManualFareChange] = useState(false);
    const [
        isTicketGoodsInforamtionFetching,
        setIsTicketGoodsInforamtionFetching,
    ] = useState(false);
    const [isGoodsInformatinSaving, setIsGoodsInformatinSaving] =
        useState(false);

    const [numberOfSeats, setNumberOfSeats] = useState("");
    const [previousDiscount, setPreviousDiscount] = useState(0);

    const [errors, setErrors] = useState({
        ticketNo: "",
        units: "",
        grossPay: "",
        netPay: "",
        finalPay: "",
        totalPay: "",
        refund: "",
    });

    const findPolicy = async () => {
        try {
            let policy = await findLatestGoodsPolicy(companyId);
            policy = policy.data.data ?? {};
            setRate(policy?.rate);
            setPerSeatFree(policy?.per_seat_free);
            setIsManualFareChange(policy?.fare_edit_option === "manual");
        } catch (e) {
            console.log(e);
        }
    };

    useEffect(() => {
        if (visible) {
            findPolicy();
        }
    }, [companyId, visible]);

    useEffect(() => {
        if (units <= perSeatFree * numberOfSeats) {
            setGrossPay(0);
            setNetPay(0);
            setFinalPay(0);
        } else {
            let calculatedUnit = units - perSeatFree * numberOfSeats;
            const calculatedValue = calculatedUnit * rate;
            setGrossPay(calculatedValue);
            setNetPay(calculatedValue - previousDiscount);
            setFinalPay(calculatedValue - previousDiscount - previouslyPaid);
        }
    }, [units]);

    const onTicketEnter = async (e) => {
        const ticketNumber = e.target.value;
        if (ticketNumber) {
            setIsTicketGoodsInforamtionFetching(true);
            try {
                const response = await getGoodsInformationByTicketNumber(
                    ticketNumber
                );

                const goodsInfo = response.data?.data;
                if (goodsInfo && !Array.isArray(goodsInfo)) {
                    setNumberOfSeats(goodsInfo?.no_of_seats);
                    setPreviousDiscount(
                        goodsInfo?.gross_pay - goodsInfo?.final_net_pay || 0
                    );
                    setUnits(goodsInfo.goods_weight_kg || 0);
                    setGrossPay(goodsInfo.gross_pay || 0);
                    setNetPay(goodsInfo.final_net_pay || 0);
                    setPreviouslyPaid(goodsInfo.final_net_pay || 0);
                }
            } catch (e) {
                toast.error({ detail: getAPIErrorMessage(e) });
            }
            setIsTicketGoodsInforamtionFetching(false);
        }
    };

    const resetFields = () => {
        setTicketNo("");
        setUnits("");
        setGrossPay("");
        setNetPay("");
        setFinalPay("");
        setTotalPay("");
        setRefund("");
        setPreviouslyPaid("");
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = {
            ticketNo: "",
            units: "",
            grossPay: "",
            netPay: "",
            finalPay: "",
            totalPay: "",
        };

        if (!ticketNo.trim()) {
            newErrors.ticketNo = "Ticket No is required";
            isValid = false;
        }
        if (!String(units).trim()) {
            newErrors.units = "Units is required";
            isValid = false;
        }
        if (!String(totalPay).trim()) {
            newErrors.totalPay = "Total Pay is required";
            isValid = false;
        }

        setErrors(newErrors);

        return isValid;
    };

    const validateTicketNumber = async () => {
        try {
            const response = await validateTicket(ticketNo);
            if (response.status === 200) {
                return true;
            }
            return false;
        } catch (e) {
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isGoodsInformatinSaving) return;

        setIsGoodsInformatinSaving(true);

        try {
            if (!validateForm()) {
                toast.error({ detail: "Sorry! Please provide valid data." });
                setIsGoodsInformatinSaving(false);
                return;
            }

            const isValidTicket = await validateTicketNumber();

            if (!validateForm()) {
                toast.error({ detail: "Sorry! Please provide valid data." });
                setIsGoodsInformatinSaving(false);
                return;
            }

            if (!isValidTicket) {
                toast.error({ detail: "Ticket number is not valid." });
                setIsGoodsInformatinSaving(false);
                return;
            }

            const data = {
                ticket_number: ticketNo,
                goods_weight_kg: Number(units),
                gross_pay: Number(grossPay),
                previously_paid: 0,
                final_net_pay: Number(netPay),
                total_paid: Number(totalPay),
                refund: Number(refund),
            };
            await createGoods(data);
            setVisible(false);
            resetFields();
            toast.success({ detail: "Goods saved successfully." });
        } catch (e) {
            toast.error({ detail: getAPIErrorMessage(e) });
        }

        setIsGoodsInformatinSaving(false);
    };

    useEffect(() => {
        setRefund(totalPay - finalPay > 0 ? totalPay - finalPay : 0);
    }, [finalPay, totalPay]);

    return (
        <div className="card flex justify-content-center">
            <Dialog
                header="Goods Operation:"
                draggable={false}
                visible={visible}
                style={{ width: "40vw" }}
                onHide={() => {
                    resetFields();
                    setVisible(false);
                }}
            >
                <form
                    onSubmit={handleSubmit}
                    className={twJoin(
                        "pt-1",
                        isTicketGoodsInforamtionFetching
                            ? "animate-pulse pointer-events-none"
                            : ""
                    )}
                >
                    <div className="flex flex-row items-center p-1">
                        <div className="flex-initial w-32">
                            <p>
                                Ticket No{" "}
                                <span className="text-sm font-medium text-red-600">
                                    *
                                </span>
                            </p>
                        </div>
                        <div className="flex-1">
                            <InputText
                                value={ticketNo}
                                onChange={(e) => setTicketNo(e.target.value)}
                                onBlur={onTicketEnter}
                                type="text"
                                className="p-inputtext-sm w-full h-10"
                                placeholder="Ticket No"
                            />
                            {ticketNo == "" && errors.ticketNo ? (
                                <span className="text-sm text-red-500">
                                    {errors.ticketNo}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex flex-row items-center mt-2 p-1">
                        <div className="flex-initial w-32">
                            <p>
                                Goods(KG){" "}
                                <span className="text-sm font-medium text-red-600">
                                    *
                                </span>
                            </p>
                        </div>
                        <div className="flex-1">
                            <InputText
                                value={units}
                                onChange={(e) => setUnits(e.target.value)}
                                keyfilter="int"
                                className="p-inputtext-sm w-full h-10"
                                placeholder="Goods(KG)"
                            />
                            {units < 1 && errors.units ? (
                                <span className="text-sm text-red-500">
                                    {errors.units}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex flex-row items-center mt-2 p-1">
                        <div className="flex-initial w-32">
                            <p>Gross Pay</p>
                        </div>
                        <div className="flex-1">
                            <InputText
                                value={grossPay}
                                keyfilter="int"
                                className="p-inputtext-sm w-full h-10"
                                onChange={(e) => {
                                    if (isManualFareChange)
                                        setGrossPay(e.target.value);
                                }}
                                placeholder="Gross Pay"
                            />
                            {errors.grossPay ? (
                                <span className="text-sm text-red-500">
                                    {errors.grossPay}
                                </span>
                            ) : null}
                        </div>
                    </div>

                    <div className="flex flex-row items-cente mt-2 p-1">
                        <div className="flex-initial w-32">
                            <p>Net Pay</p>
                        </div>
                        <div className="flex-1">
                            <InputText
                                value={netPay}
                                keyfilter="int"
                                className="p-inputtext-sm w-full h-10"
                                placeholder="Net Pay"
                            />
                            {errors.netPay ? (
                                <span className="text-sm text-red-500">
                                    {errors.netPay}
                                </span>
                            ) : null}
                        </div>
                    </div>

                    <div className="flex flex-row items-cente mt-2 bg-yellow-100 rounded p-1">
                        <div className="flex-initial w-32">
                            <p>Previously Paid</p>
                        </div>
                        <div className="flex-1">
                            <InputText
                                disabled
                                value={previouslyPaid}
                                keyfilter="int"
                                className="p-inputtext-sm w-full h-10"
                                placeholder="Previous Payed"
                            />
                        </div>
                    </div>
                    <div className="flex flex-row items-cente mt-2 p-1">
                        <div className="flex-initial w-32">
                            <p>Final Net Pay</p>
                        </div>
                        <div className="flex-1">
                            <InputText
                                value={finalPay}
                                keyfilter="int"
                                className="p-inputtext-sm w-full h-10"
                                placeholder="Final Net Pay"
                            />
                            {errors.finalPay ? (
                                <span className="text-sm text-red-500">
                                    {errors.finalPay}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex flex-row items-cente mt-2 p-1">
                        <div className="flex-initial w-32">
                            <p>
                                Total Paid{" "}
                                <span className="text-sm font-medium text-red-600">
                                    *
                                </span>
                            </p>
                        </div>
                        <div className="flex-1">
                            <InputText
                                value={totalPay}
                                onChange={(e) => setTotalPay(e.target.value)}
                                keyfilter="int"
                                className="p-inputtext-sm w-full h-10"
                                placeholder="Total Paid"
                            />
                            {errors.totalPay ? (
                                <span className="text-sm text-red-500">
                                    {errors.totalPay}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex flex-row items-cente mt-2 p-1">
                        <div className="flex-initial w-32">
                            <p>Refund</p>
                        </div>
                        <div className="flex-1">
                            <InputText
                                value={refund}
                                keyfilter="int"
                                className="p-inputtext-sm w-full h-10"
                                placeholder="Refund"
                                readOnly
                            />
                        </div>
                    </div>
                    <div className="flex flex-row items-cente mt-5">
                        <button
                            type="submit"
                            className="w-full inline-flex gap-x-1.5 items-center justify-center cursor-pointer transition duration-200 ease-in-out focus:outline-none focus:outline-offset-0 text-white font-bold bg-blue-500 hover:bg-blue-600 border border-blue-500 hover:border-blue-600 rounded-md text-sm py-2.5 disabled:bg-blue-300 disabled:border-blue-300"
                            disabled={isGoodsInformatinSaving}
                        >
                            {isGoodsInformatinSaving ? (
                                <ArrowPathIcon className="w-4 h-4 text-white animate-spin" />
                            ) : null}
                            <p>Save</p>
                        </button>
                    </div>
                </form>
            </Dialog>
        </div>
    );
}
